import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDMY } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RefreshCw, CheckCircle2, XCircle, ChevronRight } from "lucide-react";

export default function SummaryPage() {
  const [rows, setRows] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => api.get("/summary").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/logs/refresh");
      toast.success(data.note || `${data.updated} statuses updated`);
      load();
    } finally { setRefreshing(false); }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
        <div>
          <p className="uppercase text-xs tracking-[0.3em] text-muted-foreground mb-2">Reports</p>
          <h1 className="font-head text-4xl sm:text-5xl font-800 tracking-tight leading-none">Delivery Summary</h1>
          <p className="text-muted-foreground mt-3">Last 7 days, one row per day. Tap a row for the full list and reasons.</p>
        </div>
        <Button variant="secondary" onClick={refresh} disabled={refreshing} data-testid="refresh-status-button" className="gap-2 w-fit">
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh statuses
        </Button>
      </div>

      <Card className="overflow-hidden hidden sm:block">
        <div className="overflow-x-auto">
        <Table data-testid="summary-table" className="min-w-[560px]">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Date</TableHead>
              <TableHead>Day</TableHead>
              <TableHead className="text-center">Successful</TableHead>
              <TableHead className="text-center">Unsuccessful</TableHead>
              <TableHead className="text-right">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No alerts sent yet.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow
                key={r.date}
                data-testid={`summary-row-${r.date}`}
                onClick={() => setDetail(r)}
                className="odd:bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors"
              >
                <TableCell className="font-medium">{fmtDMY(r.date)}</TableCell>
                <TableCell className="text-muted-foreground">{r.day}</TableCell>
                <TableCell className="text-center">
                  <Badge className="rounded-full bg-[hsl(var(--success))]" data-testid={`success-count-${r.date}`}>{r.success}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge className="rounded-full bg-destructive" data-testid={`failed-count-${r.date}`}>{r.failed}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" data-testid={`view-details-${r.date}`} className="gap-1 text-primary">
                    View <ChevronRight className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </Card>

      {/* Mobile stacked cards */}
      <div className="sm:hidden space-y-3" data-testid="summary-cards">
        {rows.length === 0 && (
          <Card className="p-8 text-center text-muted-foreground">No alerts sent yet.</Card>
        )}
        {rows.map((r) => (
          <Card
            key={r.date}
            data-testid={`summary-card-${r.date}`}
            onClick={() => setDetail(r)}
            className="p-4 cursor-pointer transition-colors hover:bg-muted/40"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium">{fmtDMY(r.date)}</p>
                <p className="text-sm text-muted-foreground">{r.day}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="rounded-full bg-[hsl(var(--success))]" data-testid={`success-count-m-${r.date}`}>{r.success} ok</Badge>
                <Badge className="rounded-full bg-destructive" data-testid={`failed-count-m-${r.date}`}>{r.failed} failed</Badge>
                <ChevronRight className="h-4 w-4 text-primary shrink-0" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="summary-detail-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head">{detail?.day}, {fmtDMY(detail?.date)}</DialogTitle>
            <DialogDescription>
              {detail?.success} successful · {detail?.failed} unsuccessful
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto divide-y divide-border rounded-md border border-border">
            {detail?.details?.map((d, i) => (
              <div key={i} className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm" data-testid={`detail-row-${i}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{d.staff_name}</p>
                  <p className="text-xs text-muted-foreground">{d.phone}</p>
                </div>
                <div className={`shrink-0 inline-flex items-center gap-1.5 text-right ${d.ok ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                  {d.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  <span className="text-xs">{d.reason}</span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
